// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import config from "@config";
import moment from "moment";

import { Icons, IconButton, Button, SectionComponent, ColumnsContainer, View } from "@components";
import actionCreator from "@modules/reporting/actionCreator";
import { ManagerReferralTaskForm } from "@modules/shared/components";
import type { ReferralDecisionTask } from "@modules/shared/reducer/absence-review/types";

type OwnProps = $Exact<{
  toggleTasksSection: () => void,
  isTasksHidden: boolean,
  selectedTask: ReferralDecisionTask,
  clientId: number,
}>;

type PropsFromDispatch = $Exact<{
  submitReview: typeof actionCreator.SubmitAbsenceTask,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromDispatch,
}>;

type State = $Exact<{
  isOpen: {
    employeeDetails: boolean,
    referralDetails: boolean,
  },
}>;

export class ManagerReferralDecisionTask extends React.Component<Props, State> {
  state = {
    isOpen: {
      employeeDetails: true,
      referralDetails: true,
    },
  };

  toggleSection = (section: string) => () => {
    this.setState({
      ...this.state,
      isOpen: {
        ...this.state.isOpen,
        [section]: !this.state.isOpen[section],
      },
    });
  };

  get employeeDetailsRows() {
    return [
      {
        columns: [
          {
            data: [
              [this.props.t("EMPLOYEE_NAME"), ""],
              [this.props.t("EMPLOYEE_REF"), ""],
              [this.props.t("JOB_TITLE"), ""],
              [this.props.t("DEPARTMENT"), ""],
            ],
          },
          {
            data: [
              [
                `${this.props.selectedTask.employee.firstName} ${this.props.selectedTask.employee.lastName}`,
                "",
              ],
              [this.props.selectedTask.employee.employeeRefPrimary, ""],
              [
                this.props.selectedTask.organisationalUnits[0]?.jobTitle ||
                  this.props.t("UNASSIGNED"),
                "",
              ],
              [this.props.selectedTask.organisationalUnits[0]?.name, ""],
            ],
          },
        ],
      },
    ];
  }

  get referralDetailsRows() {
    return [
      {
        columns: [
          {
            data: [
              [this.props.t("Date Absence Reported"), ""],
              [this.props.t("Absence Start Date/Time"), ""],
              [this.props.t("RTW Date Date/Time"), ""],
              [this.props.t("Working Days Lost"), ""],
              [this.props.t("Working Hours Lost"), ""],
              [this.props.t("Absence Reason"), ""],
              [this.props.t("Alert Triggered"), ""],
              [this.props.t("Supplier Name"), ""],
              [this.props.t("Supplier Description"), ""],
            ],
          },
          {
            data: [
              [moment(this.props.selectedTask.reportedAt).format(config.dateTimeFormat) || "", ""],
              [moment(this.props.selectedTask.absenceStart).format(config.dateTimeFormat), ""],
              [moment(this.props.selectedTask.rtwDate).format(config.dateTimeFormat), ""],
              [this.props.selectedTask.daysLost, ""],
              [this.props.selectedTask.hoursLost || 0, ""],
              [this.props.selectedTask.absenceReason.label, ""],
              [this.props.selectedTask.alertName, ""],
              [(this.props.selectedTask.referralSupplier || {}).name, ""],
              [(this.props.selectedTask.referralSupplier || {}).description, ""],
            ],
          },
        ],
      },
    ];
  }

  render() {
    if (!this.props.selectedTask) return <div />;
    return (
      <View name="ManagerReferralDecisionTask">
        {/*Employee Details*/}
        <SectionComponent
          title={<h6>{this.props.t("EMPLOYEE_DETAILS")}</h6>}
          className="gs-container"
          wrapperClassName="mt-0"
          collapsable
          isOpen={this.state.isOpen.employeeDetails}
          onTitleClick={this.toggleSection("employeeDetails")}
        >
          <ColumnsContainer rows={this.employeeDetailsRows} className="no-gs-container" />
        </SectionComponent>

        {/*Referral Details*/}
        <SectionComponent
          title={<h6>{this.props.t("REFERRAL_DETAILS")}</h6>}
          collapsable
          className="mt-4 mb-0 gs-container"
          wrapperClassName="mt-0"
          isOpen={this.state.isOpen.referralDetails}
          onTitleClick={this.toggleSection("referralDetails")}
        >
          <ColumnsContainer rows={this.referralDetailsRows} className="no-gs-container" />
        </SectionComponent>

        {/*Referral Form*/}
        <SectionComponent
          title={<h6>{this.props.t("REFERRAL_FORM")}</h6>}
          collapsable
          className="mt-4 mb-4 gs-container"
          isOpen={this.state.isOpen.referralDetails}
          onTitleClick={this.toggleSection("referralDetails")}
        >
          <ManagerReferralTaskForm clientId={this.props.clientId} task={this.props.selectedTask} />
        </SectionComponent>
      </View>
    );
  }
}

const mapDispatchToProps: PropsFromDispatch = {
  submitReview: actionCreator.SubmitAbsenceTask,
};

export default compose(
  localize(),
  connect(undefined, mapDispatchToProps),
)(ManagerReferralDecisionTask);
